import { create } from "zustand";
import type { DynamicFormSchema } from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import type { FormEditorEntity } from "../form-editor-entity.types";
import { createDefaultField } from "../lib/create-default-field";
import {
	dynamicFormSchemaToBuilderState,
	resolveSchemaLayout,
} from "../lib/dynamic-schema-to-builder";
import { sectionHasRoomForAnotherField } from "../lib/section-field-limit";
import type { PaletteFieldType } from "../types";
import {
	type BuilderFieldData,
	type BuilderNode,
	type BuilderSectionData,
	createDefaultSection,
	type TicketTypeFormBuilderState,
} from "../types";
import type { FormRule } from "../types/form-rule-types";

function newId(): string {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const initialCanvasState = (): TicketTypeFormBuilderState & {
	formRules: FormRule[];
} => ({
	formTitle: "",
	formDescription: "",
	rootIds: [],
	nodes: {},
	selectedId: null,
	formRules: [],
});

/**
 * Returns `true` when a {@link FormRule} references any field id from `removed`
 * in its conditions or actions.
 */
function ruleReferencesAnyId(rule: FormRule, removed: Set<string>): boolean {
	for (const c of rule.conditions) {
		if (removed.has(c.fieldDefinitionId)) return true;
	}
	for (const a of rule.actions) {
		if (removed.has(a.targetFieldDefinitionId)) return true;
	}
	return false;
}

/**
 * Filters out conditions/actions that reference removed field ids. Returns a
 * new rule, or `null` if pruning leaves the rule with zero actions (invalid).
 */
function pruneRuleForRemovedFieldIds(
	rule: FormRule,
	removed: Set<string>,
): FormRule | null {
	if (!ruleReferencesAnyId(rule, removed)) return rule;
	const conditions = rule.conditions.filter(
		(c) => !removed.has(c.fieldDefinitionId),
	);
	const actions = rule.actions.filter(
		(a) => !removed.has(a.targetFieldDefinitionId),
	);
	if (actions.length === 0) return null;
	return { ...rule, conditions, actions };
}

function cascadePruneRules(
	rules: FormRule[],
	removed: Set<string>,
): FormRule[] {
	if (removed.size === 0) return rules;
	const next: FormRule[] = [];
	for (const r of rules) {
		const pruned = pruneRuleForRemovedFieldIds(r, removed);
		if (pruned !== null) next.push(pruned);
	}
	return next;
}

export type HydrateFromSchemaResult =
	| { ok: true; warnings: string[] }
	| { ok: false; errors: string[] };

export interface FormEditorStore extends TicketTypeFormBuilderState {
	/** Drives ticket vs job editor behavior; both use `admin.shared` locale keys. */
	formEditorEntity: FormEditorEntity;
	/**
	 * Cross-field form rules persisted in the layoutJson envelope as `formRules`.
	 * Mutated through {@link addFormRule}/{@link updateFormRule}/{@link removeFormRule}/{@link setFormRuleEnabled}.
	 * Cascade-cleaned when fields are removed via {@link deleteNode}/{@link clearSectionFields}.
	 */
	formRules: FormRule[];
	setFormEditorEntity: (entity: FormEditorEntity) => void;
	reset: () => void;
	/**
	 * Clears the canvas and reapplies a saved schema (or leaves empty when `schema` is undefined).
	 * Used on Cancel and on editor hydration so the canvas matches the last loaded API definition
	 * without requiring a remount when the hydration key is unchanged.
	 */
	revertToSavedSchema: (
		schema: DynamicFormSchema | undefined,
		formRules?: FormRule[],
	) => HydrateFromSchemaResult;
	/**
	 * Replaces canvas state from a {@link DynamicFormSchema} (e.g. saved draft).
	 * Does not call {@link reset} first — callers should reset if needed.
	 */
	hydrateFromSchema: (schema: DynamicFormSchema) => HydrateFromSchemaResult;
	/** Replaces the entire {@link FormEditorStore.formRules} list (e.g. on hydration). */
	hydrateFormRules: (rules: FormRule[]) => void;
	/** Appends a new {@link FormRule}. */
	addFormRule: (rule: FormRule) => void;
	/** Updates a {@link FormRule} by id; ignored when no rule matches. */
	updateFormRule: (id: string, partial: Partial<FormRule>) => void;
	/** Removes the rule with the given id. */
	removeFormRule: (id: string) => void;
	/** Toggles the `enabled` flag on a rule by id. */
	setFormRuleEnabled: (id: string, enabled: boolean) => void;
	/** Updates schema title and description on the projected {@link DynamicFormSchema}. */
	setFormMeta: (partial: {
		formTitle?: string;
		formDescription?: string;
	}) => void;
	setSelectedId: (id: string | null) => void;
	addSection: (defaultSectionLabel: string) => void;
	addFieldFromPalette: (
		fieldType: PaletteFieldType,
		target: { parent: "root" | string; index?: number },
		defaultFieldLabel: string,
	) => boolean;
	updateField: (id: string, partial: Partial<BuilderFieldData>) => void;
	updateSection: (id: string, partial: Partial<BuilderSectionData>) => void;
	deleteNode: (id: string) => void;
	/** Removes every field in the section; the section node stays. */
	clearSectionFields: (sectionId: string) => void;
	moveRoot: (activeId: string, overId: string) => void;
	reorderRoot: (orderedIds: string[]) => void;
	reorderInSection: (sectionId: string, orderedChildIds: string[]) => void;
	moveFieldToParent: (
		fieldId: string,
		newParent: "root" | string,
		index: number,
	) => boolean;
	moveSection: (sectionId: string, direction: "up" | "down") => void;
	/** Moves a field one position within its parent list (root or section). */
	moveField: (fieldId: string, direction: "up" | "down") => void;
}

export const useFormEditorStore = create<FormEditorStore>((set, get) => ({
	...initialCanvasState(),
	formEditorEntity: "ticket",
	setFormEditorEntity: (formEditorEntity) => {
		set({ formEditorEntity });
	},

	reset: () => {
		set((s) => ({
			...initialCanvasState(),
			formEditorEntity: s.formEditorEntity,
		}));
	},

	revertToSavedSchema: (schema, formRules) => {
		set((s) => ({
			...initialCanvasState(),
			formEditorEntity: s.formEditorEntity,
		}));
		if (formRules !== undefined) {
			set({ formRules });
		}
		if (schema == null) {
			return { ok: true, warnings: [] };
		}
		return get().hydrateFromSchema(schema);
	},

	hydrateFromSchema: (schema) => {
		if (!schema || !Array.isArray(schema.fieldDefinitions)) {
			return {
				ok: false,
				errors: ["Invalid schema: fieldDefinitions must be an array"],
			};
		}
		const layoutResult = resolveSchemaLayout(schema);
		if (!layoutResult.ok) {
			return {
				ok: false,
				errors: [layoutResult.error],
			};
		}
		const { formTitle, formDescription, rootIds, nodes, selectedId, warnings } =
			dynamicFormSchemaToBuilderState(schema, get().formEditorEntity);
		set({ formTitle, formDescription, rootIds, nodes, selectedId });
		return { ok: true, warnings };
	},

	hydrateFormRules: (rules) => {
		set({ formRules: rules });
	},

	addFormRule: (rule) => {
		set((s) => ({ formRules: [...s.formRules, rule] }));
	},

	updateFormRule: (id, partial) => {
		set((s) => {
			const idx = s.formRules.findIndex((r) => r.id === id);
			if (idx === -1) return s;
			const next = [...s.formRules];
			next[idx] = { ...next[idx], ...partial };
			return { formRules: next };
		});
	},

	removeFormRule: (id) => {
		set((s) => ({ formRules: s.formRules.filter((r) => r.id !== id) }));
	},

	setFormRuleEnabled: (id, enabled) => {
		set((s) => {
			const idx = s.formRules.findIndex((r) => r.id === id);
			if (idx === -1) return s;
			const next = [...s.formRules];
			next[idx] = { ...next[idx], enabled };
			return { formRules: next };
		});
	},

	setFormMeta: (partial) => {
		set((s) => ({
			formTitle:
				partial.formTitle !== undefined ? partial.formTitle : s.formTitle,
			formDescription:
				partial.formDescription !== undefined
					? partial.formDescription
					: s.formDescription,
		}));
	},

	setSelectedId: (selectedId) => {
		set({ selectedId });
	},

	addSection: (defaultSectionLabel) => {
		const id = newId();
		const section = createDefaultSection(
			id,
			defaultSectionLabel,
			get().formEditorEntity,
		);
		set((s) => ({
			rootIds: [...s.rootIds, id],
			nodes: {
				...s.nodes,
				[id]: {
					kind: "section",
					section,
					childFieldIds: [],
				},
			},
			selectedId: id,
		}));
	},

	addFieldFromPalette: (fieldType, target, defaultFieldLabel) => {
		let added = false;
		set((s) => {
			if (target.parent !== "root") {
				const parent = s.nodes[target.parent];
				if (
					parent?.kind === "section" &&
					!sectionHasRoomForAnotherField(
						parent.section,
						parent.childFieldIds.length,
					)
				) {
					return s;
				}
			}

			const id = newId();
			const field = createDefaultField(
				fieldType,
				id,
				defaultFieldLabel,
				get().formEditorEntity,
			);
			const nodes = { ...s.nodes };
			const fieldNode: BuilderNode = {
				kind: "field",
				field,
				parentId: target.parent === "root" ? "root" : target.parent,
			};
			nodes[id] = fieldNode;

			let rootIds = [...s.rootIds];
			if (target.parent === "root") {
				const insertAt =
					target.index !== undefined
						? Math.min(target.index, rootIds.length)
						: rootIds.length;
				rootIds = [
					...rootIds.slice(0, insertAt),
					id,
					...rootIds.slice(insertAt),
				];
			} else {
				const parent = nodes[target.parent];
				if (parent?.kind === "section") {
					const childFieldIds = [...parent.childFieldIds];
					const insertAt =
						target.index !== undefined
							? Math.min(target.index, childFieldIds.length)
							: childFieldIds.length;
					childFieldIds.splice(insertAt, 0, id);
					nodes[target.parent] = {
						...parent,
						childFieldIds,
					};
				}
			}

			added = true;
			return {
				nodes,
				rootIds,
				selectedId: id,
			};
		});
		return added;
	},

	updateField: (id, partial) => {
		set((s) => {
			const node = s.nodes[id];
			if (!node || node.kind !== "field") return s;
			return {
				nodes: {
					...s.nodes,
					[id]: {
						...node,
						field: { ...node.field, ...partial },
					},
				},
			};
		});
	},

	updateSection: (id, partial) => {
		set((s) => {
			const node = s.nodes[id];
			if (!node || node.kind !== "section") return s;
			return {
				nodes: {
					...s.nodes,
					[id]: {
						...node,
						section: { ...node.section, ...partial },
					},
				},
			};
		});
	},

	deleteNode: (id) => {
		set((s) => {
			const node = s.nodes[id];
			if (!node) return s;

			const nodes: Record<string, BuilderNode> = { ...s.nodes };
			let rootIds = [...s.rootIds];
			const removedFieldIds = new Set<string>();

			if (node.kind === "section") {
				for (const childId of node.childFieldIds) {
					removedFieldIds.add(childId);
					delete nodes[childId];
				}
				delete nodes[id];
				rootIds = rootIds.filter((rid) => rid !== id);
			} else {
				removedFieldIds.add(id);
				if (node.parentId === "root") {
					rootIds = rootIds.filter((rid) => rid !== id);
				} else {
					const parent = nodes[node.parentId];
					if (parent?.kind === "section") {
						nodes[node.parentId] = {
							...parent,
							childFieldIds: parent.childFieldIds.filter((cid) => cid !== id),
						};
					}
				}
				delete nodes[id];
			}

			return {
				nodes,
				rootIds,
				selectedId: s.selectedId === id ? null : s.selectedId,
				formRules: cascadePruneRules(s.formRules, removedFieldIds),
			};
		});
	},

	clearSectionFields: (sectionId) => {
		set((s) => {
			const parent = s.nodes[sectionId];
			if (!parent || parent.kind !== "section") return s;
			if (parent.childFieldIds.length === 0) return s;

			const nodes: Record<string, BuilderNode> = { ...s.nodes };
			const removed = new Set(parent.childFieldIds);
			for (const childId of parent.childFieldIds) {
				delete nodes[childId];
			}
			nodes[sectionId] = {
				...parent,
				childFieldIds: [],
			};

			const selectedId =
				s.selectedId !== null && removed.has(s.selectedId)
					? null
					: s.selectedId;

			return {
				nodes,
				selectedId,
				formRules: cascadePruneRules(s.formRules, removed),
			};
		});
	},

	moveRoot: (activeId, overId) => {
		set((s) => {
			const oldIndex = s.rootIds.indexOf(activeId);
			const newIndex = s.rootIds.indexOf(overId);
			if (oldIndex === -1 || newIndex === -1) return s;
			const next = [...s.rootIds];
			next.splice(oldIndex, 1);
			next.splice(newIndex, 0, activeId);
			return { rootIds: next };
		});
	},

	reorderRoot: (orderedIds) => {
		set({ rootIds: orderedIds });
	},

	reorderInSection: (sectionId, orderedChildIds) => {
		set((s) => {
			const parent = s.nodes[sectionId];
			if (!parent || parent.kind !== "section") return s;
			return {
				nodes: {
					...s.nodes,
					[sectionId]: {
						...parent,
						childFieldIds: orderedChildIds,
					},
				},
			};
		});
	},

	moveFieldToParent: (fieldId, newParent, index) => {
		let moved = false;
		set((s) => {
			const node = s.nodes[fieldId];
			if (!node || node.kind !== "field") return s;

			if (newParent !== "root") {
				const targetParent = s.nodes[newParent];
				if (targetParent?.kind === "section") {
					const alreadyInTarget = node.parentId === newParent;
					if (
						!alreadyInTarget &&
						!sectionHasRoomForAnotherField(
							targetParent.section,
							targetParent.childFieldIds.length,
						)
					) {
						return s;
					}
				}
			}

			const nodes = { ...s.nodes };
			let rootIds = [...s.rootIds];

			if (node.parentId === "root") {
				rootIds = rootIds.filter((id) => id !== fieldId);
			} else {
				const prevParent = nodes[node.parentId];
				if (prevParent?.kind === "section") {
					nodes[node.parentId] = {
						...prevParent,
						childFieldIds: prevParent.childFieldIds.filter(
							(id) => id !== fieldId,
						),
					};
				}
			}

			if (newParent === "root") {
				const insertAt = Math.min(index, rootIds.length);
				rootIds.splice(insertAt, 0, fieldId);
				nodes[fieldId] = {
					...node,
					parentId: "root",
				};
			} else {
				const parent = nodes[newParent];
				if (parent?.kind === "section") {
					const childFieldIds = [...parent.childFieldIds];
					const insertAt = Math.min(index, childFieldIds.length);
					childFieldIds.splice(insertAt, 0, fieldId);
					nodes[newParent] = { ...parent, childFieldIds };
					nodes[fieldId] = {
						...node,
						parentId: newParent,
					};
				}
			}

			moved = true;
			return { nodes, rootIds, selectedId: fieldId };
		});
		return moved;
	},

	moveSection: (sectionId, direction) => {
		set((s) => {
			const idx = s.rootIds.indexOf(sectionId);
			if (idx === -1) return s;
			const swapWith = direction === "up" ? idx - 1 : idx + 1;
			if (swapWith < 0 || swapWith >= s.rootIds.length) return s;
			const next = [...s.rootIds];
			[next[idx], next[swapWith]] = [next[swapWith], next[idx]];
			return { rootIds: next };
		});
	},

	moveField: (fieldId, direction) => {
		set((s) => {
			const node = s.nodes[fieldId];
			if (!node || node.kind !== "field") return s;

			if (node.parentId === "root") {
				const idx = s.rootIds.indexOf(fieldId);
				if (idx === -1) return s;
				const swapWith = direction === "up" ? idx - 1 : idx + 1;
				if (swapWith < 0 || swapWith >= s.rootIds.length) return s;
				const next = [...s.rootIds];
				[next[idx], next[swapWith]] = [next[swapWith], next[idx]];
				return { rootIds: next };
			}

			const parent = s.nodes[node.parentId];
			if (!parent || parent.kind !== "section") return s;
			const childIds = parent.childFieldIds;
			const idx = childIds.indexOf(fieldId);
			if (idx === -1) return s;
			const swapWith = direction === "up" ? idx - 1 : idx + 1;
			if (swapWith < 0 || swapWith >= childIds.length) return s;
			const nextChildren = [...childIds];
			[nextChildren[idx], nextChildren[swapWith]] = [
				nextChildren[swapWith],
				nextChildren[idx],
			];
			return {
				nodes: {
					...s.nodes,
					[node.parentId]: {
						...parent,
						childFieldIds: nextChildren,
					},
				},
			};
		});
	},
}));
